import React from 'react';
import { paramCase } from 'change-case';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownItemProps } from 'semantic-ui-react';
import styled, { css } from 'styled-components';
import { Preview as ContactPreview } from '@/components/pages/Subscription/Presentation/Preview/ContactPreview/Preview';
import { Contact, summarize } from '@/types/contact';
import { AnchorLike } from '@/components/shared/AnchorLike';
import { Label } from '@/components/shared/Label';

type Props = {
  contacts: Contact[];
  error?: string;
  id: string;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  target?: 'billingContactId' | 'contactId';
  onChange: (contactId: number | undefined, touched: boolean) => void;
  value?: number;
  isContactExist: boolean;
  setModalMode?: React.Dispatch<React.SetStateAction<'new' | 'edit'>>;
  setContactToEdit?: React.Dispatch<React.SetStateAction<Contact | undefined>>;
};

export const ContactView: React.FC<Props> = ({
  contacts,
  error = '',
  id,
  setIsOpen,
  target,
  onChange,
  value,
  isContactExist,
  setModalMode,
  setContactToEdit,
}) => {
  const { t } = useTranslation();
  const addLabel =
    target === 'contactId'
      ? t('ui.subscription.contact.add')
      : t('ui.subscription.contact.addBillingDestination');
  const options: DropdownItemProps[] = contacts.map((contact) => {
    return {
      active: contact.id === value,
      key: contact.id,
      onClick: () => onChange(contact.id, true),
      text: summarize(contact, '　'),
      value: contact.id,
    };
  });
  options.push({
    className: 'addContact',
    key: -1,
    onClick: () => {
      setIsOpen(true);

      if (setModalMode !== undefined) {
        setModalMode('new');
      }
    },
    text: addLabel,
  });

  const currentContact =
    contacts.find((contact) => contact.id === value) ||
    contacts[contacts.length - 1];

  return (
    <>
      <Styled>
        <div
          className={classnames({
            'contact-field': true,
            'not-exist-error': !isContactExist,
          })}
        >
          {isContactExist &&
            (currentContact !== undefined ? (
              <Dropdown
                id={id}
                disabled={!isContactExist}
                data-test={paramCase(id)}
                options={options}
                selection={true}
                text={summarize(currentContact, '　')}
                value={value}
              />
            ) : (
              <Dropdown
                id={id}
                data-test={paramCase(id)}
                options={options}
                selection={true}
                value={value}
              />
            ))}
          {error && <Label>{error}</Label>}
          {isContactExist && currentContact.id > 0 && (
            <ContactPreview
              contact={currentContact}
              setIsOpen={setIsOpen}
              setModalMode={setModalMode}
              setContactToEdit={setContactToEdit}
            />
          )}
          {!isContactExist && (
            <StyledAddContactButton
              onClick={() => {
                setIsOpen(true);
              }}
            >
              {addLabel}
            </StyledAddContactButton>
          )}
        </div>
      </Styled>
    </>
  );
};

const ErrorTagStyle = css`
  border: 1px solid #e0b4b4;
  background-color: #fff6f6;
  color: #9f3a38;
`;

const Styled = styled.div`
  .ui.selection.dropdown {
    display: block;
  }

  .contact-field .ui.selection.dropdown .text {
    display: block;
    overflow: hidden;
    width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .not-exist-error .disabled {
    ${ErrorTagStyle};
  }

  .ui.selection.dropdown .menu > .item.addContact {
    text-align: center;
    color: #4183c4;
  }
`;

const StyledAddContactButton = styled(AnchorLike)`
  cursor: pointer;
  display: block;
  width: 100%;
  padding: 1em;
  margin-top: 1em;
  background: rgb(0, 0, 0, 0.05);
  box-shadow: none;
  text-align: center;
  ${ErrorTagStyle};
`;
